import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MatchingScore } from '@inversor-hub/types';

interface MatchingIndicatorProps {
  score: MatchingScore;
}

const IndicatorContainer = styled(Box)(() => ({
  position: 'absolute',
  bottom: 8,
  right: 8,
  zIndex: 2,
}));

const ScoreChip = styled(Chip)<{ matchscore: MatchingScore }>(({ theme, matchscore }) => {
  const colors = {
    HIGH: {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    },
    MEDIUM: {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.warning.contrastText,
    },
    LOW: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
    },
  };

  return {
    ...colors[matchscore],
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 24,
    '& .MuiChip-label': {
      padding: '0 8px',
    },
  };
});

const TooltipContent = styled(Box)<{ matchscore: MatchingScore }>(({ theme, matchscore }) => {
  const colors = {
    HIGH: theme.palette.success.main,
    MEDIUM: theme.palette.warning.main,
    LOW: theme.palette.error.main,
  };

  return {
    padding: theme.spacing(1),
    minWidth: 200,
    backgroundColor: colors[matchscore],
    color: theme.palette.getContrastText(colors[matchscore]),
    borderRadius: theme.shape.borderRadius,
  };
});

export const MatchingIndicator: React.FC<MatchingIndicatorProps> = ({ score }) => {
  const getScoreLabel = (score: MatchingScore): string => {
    switch (score) {
      case 'HIGH':
        return '🟢';
      case 'MEDIUM':
        return '🟡';
      case 'LOW':
        return '🔴';
    }
  };

  const getScoreText = (score: MatchingScore): string => {
    switch (score) {
      case 'HIGH':
        return 'Se ajusta a tus preferencias';
      case 'MEDIUM':
        return 'Se ajusta parcialmente a tus preferencias';
      case 'LOW':
        return 'Esta oportunidad no se ajusta a tus preferencias';
    }
  };

  const tooltipContent = (
    <TooltipContent matchscore={score}>
      <Typography variant="subtitle2" sx={{ color: 'white' }}>
        {getScoreText(score)}
      </Typography>
    </TooltipContent>
  );

  return (
    <IndicatorContainer>
      <Tooltip
        title={tooltipContent}
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'transparent',
              border: 'none',
              boxShadow: 'none',
              p: 0,
            },
          },
          arrow: {
            sx: {
              color:
                score === 'HIGH'
                  ? 'success.main'
                  : score === 'MEDIUM'
                    ? 'warning.main'
                    : 'error.main',
            },
          },
        }}
      >
        <ScoreChip matchscore={score} label={getScoreLabel(score)} size="small" />
      </Tooltip>
    </IndicatorContainer>
  );
};
